import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ScrollToHash } from './components/ScrollToHash';
import { AuthProvider, ProtectedRoute } from './components/auth';
import { LandingSkeleton, AppSkeleton } from './components/skeletons';
import { PluginBootstrapper } from './plugins/PluginBootstrapper';
import { builtinPlugins } from './plugins/builtinPlugins';
import { usePluginRoutes } from './plugins/usePluginRoutes';
import { WorkspaceGate } from './app/components/WorkspaceGate';
import { APP_VERSION } from './app/version';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RoleGate } from './auth/RoleGate';
import { useWorkspaceCapabilities } from './hooks/useWorkspaceCapabilities';
import { pluginRegistry } from './plugins/pluginRegistry';
import './App.css';

// Lazy-loaded route components for code splitting
// Public routes - bundled into 'landing' chunk
const LandingPage = lazy(() => import('./components/landing/LandingPage').then(m => ({ default: m.LandingPage })));
const Pricing = lazy(() => import('./routes/public/Pricing'));
const Contact = lazy(() => import('./routes/public/Contact'));
const Impressum = lazy(() => import('./routes/public/Impressum'));
const Datenschutz = lazy(() => import('./routes/public/Datenschutz'));

// Auth routes - bundled into 'app' chunk
const LoginPage = lazy(() => import('./routes/auth/LoginPage'));

// App routes - bundled into 'app' chunk
const AppShell = lazy(() => import('./app/layout/AppShell').then(m => ({ default: m.AppShell })));
const PluginMarketplace = lazy(() => import('./app/pages/PluginMarketplace').then(m => ({ default: m.PluginMarketplace })));
const WorkspaceOnboarding = lazy(() => import('./app/pages/WorkspaceOnboarding').then(m => ({ default: m.WorkspaceOnboarding })));
const WorkspaceManagerPage = lazy(() => import('./app/pages/WorkspaceManagerPage').then(m => ({ default: m.WorkspaceManagerPage })));
const DomainManagerPage = lazy(() => import('./app/pages/DomainManagerPage').then(m => ({ default: m.DomainManagerPage })));
const OrgMembersPage = lazy(() => import('./app/pages/OrgMembersPage').then(m => ({ default: m.OrgMembersPage })));
const WorkspaceMembersPage = lazy(() => import('./app/pages/WorkspaceMembersPage').then(m => ({ default: m.WorkspaceMembersPage })));
const AdminMembersOverviewPage = lazy(() => import('./app/pages/AdminMembersOverviewPage').then(m => ({ default: m.AdminMembersOverviewPage })));
const HelpPage = lazy(() => import('./app/pages/HelpPage').then(m => ({ default: m.HelpPage })));
const AccessDeniedPage = lazy(() => import('./app/pages/AccessDeniedPage').then(m => ({ default: m.AccessDeniedPage })));
const SettingsPage = lazy(() => import('./app/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SettingsAccountTab = lazy(() => import('./app/features/settings/SettingsAccountTab').then(m => ({ default: m.SettingsAccountTab })));
const SettingsWorkspaceTab = lazy(() => import('./app/features/settings/SettingsWorkspaceTab').then(m => ({ default: m.SettingsWorkspaceTab })));
const SettingsWorkspaceMcpTab = lazy(() => import('./app/features/settings/SettingsWorkspaceMcpTab').then(m => ({ default: m.SettingsWorkspaceMcpTab })));
const SettingsWorkspaceKiOverridesTab = lazy(() => import('./app/features/settings/SettingsWorkspaceKiOverridesTab').then(m => ({ default: m.SettingsWorkspaceKiOverridesTab })));
const SettingsRetrievalSemanticsTab = lazy(() => import('./app/features/settings/SettingsRetrievalSemanticsTab').then(m => ({ default: m.SettingsRetrievalSemanticsTab })));
const SettingsWorkspaceMembersTab = lazy(() => import('./app/features/settings/SettingsWorkspaceMembersTab').then(m => ({ default: m.SettingsWorkspaceMembersTab })));
const SettingsPluginsTab = lazy(() => import('./app/features/settings/SettingsPluginsTab').then(m => ({ default: m.SettingsPluginsTab })));
const SettingsAppearanceTab = lazy(() => import('./app/features/settings/SettingsAppearanceTab').then(m => ({ default: m.SettingsAppearanceTab })));
const SettingsPlatformObservabilityTab = lazy(() => import('./app/features/settings/platform/SettingsPlatformObservabilityTab').then(m => ({ default: m.SettingsPlatformObservabilityTab })));
const SettingsPlatformIntegrationsTab = lazy(() => import('./app/features/settings/platform/SettingsPlatformIntegrationsTab').then(m => ({ default: m.SettingsPlatformIntegrationsTab })));
const SettingsPlatformDatabasesTab = lazy(() => import('./app/features/settings/platform/SettingsPlatformDatabasesTab').then(m => ({ default: m.SettingsPlatformDatabasesTab })));
const SettingsPlatformPipelineTab = lazy(() => import('./app/features/settings/platform/SettingsPlatformPipelineTab').then(m => ({ default: m.SettingsPlatformPipelineTab })));
// DevPlan 0120 (2026-05-20): Platform-Settings-IA-Refactor — Models- und
// System-Tab in einzelne semantische Pages aufgeteilt (KI · Infrastruktur ·
// System · Operations). Die alten Monolithen werden per Redirect ersetzt.
const KiModelsPage = lazy(() => import('./app/features/settings/platform/KiModelsPage').then(m => ({ default: m.KiModelsPage })));
const KiRoleBindingsPage = lazy(() => import('./app/features/settings/platform/KiRoleBindingsPage').then(m => ({ default: m.KiRoleBindingsPage })));
const KiVoicePage = lazy(() => import('./app/features/settings/platform/KiVoicePage').then(m => ({ default: m.KiVoicePage })));
const VersionBuildPage = lazy(() => import('./app/features/settings/platform/VersionBuildPage').then(m => ({ default: m.VersionBuildPage })));
const AuditLogPage = lazy(() => import('./app/features/settings/platform/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const ProviderHealthPage = lazy(() => import('./app/features/settings/platform/ProviderHealthPage').then(m => ({ default: m.ProviderHealthPage })));
const ConsistencyPage = lazy(() => import('./app/features/settings/platform/ConsistencyPage').then(m => ({ default: m.ConsistencyPage })));


/** Maps a plugin route path to a human-readable feature label for error UI. */
function featureNameFromPath(path?: string): string | undefined {
  if (!path) return undefined;
  const segment = path.split('/').pop() ?? path;
  const map: Record<string, string> = {
    dashboard: 'Sopho Dashboard',
    ontology: 'Sopho Ontology',
    'talk-to-data': 'Sopho Chat',
    uploader: 'Sopho Uploader',
    connectors: 'Sopho Connector',
    steward: 'Sopho Steward',
    interviewer: 'Sopho Interviewer',
    capture: 'Wissenserfassung',
    'ruleset-analyzer': 'Sopho Ruleset',
    machines: 'Sopho Machine Models',
    documents: 'Sopho Data Viewer',
    'rollout-kpi': 'Sopho Quality',
    governance: 'Sopho Governance',
    flows: 'Sopho Flows',
    'sopho-fuse': 'Sopho Fuse',
    'chunk-visualizer': 'Sopho Chunk Visualizer',
  };
  return map[segment];
}

function wrapWithRoleGate(element: React.ReactNode, routeId?: string): React.ReactNode {
  if (!routeId) return element;
  const plugin = pluginRegistry.getPluginById(routeId);
  if (!plugin?.accessControl) return element;
  return <RoleGate pluginId={routeId}>{element}</RoleGate>;
}

function MachineAwareRouteGate({ children }: { children: React.ReactNode }) {
  const { isMachineAware, settingsLoaded } = useWorkspaceCapabilities();

  if (!settingsLoaded) {
    return <div className="flex items-center justify-center h-32" data-testid="machine-aware-route-loading" />;
  }

  if (!isMachineAware) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}

function wrapWithCapabilityGate(element: React.ReactNode, routeId?: string): React.ReactNode {
  if (routeId !== 'machine-registry') {
    return element;
  }

  return <MachineAwareRouteGate>{element}</MachineAwareRouteGate>;
}

function renderPluginRoutes(routes: ReturnType<typeof usePluginRoutes>) {
  return routes
    .map((route, index) => {
      const wrappedElement = route.element ? (
        <ErrorBoundary featureName={featureNameFromPath(route.path)}>
          {route.element}
        </ErrorBoundary>
      ) : route.element;

      const roleGatedElement = wrapWithRoleGate(wrappedElement, route.id ?? undefined);
      const gatedElement = wrapWithCapabilityGate(roleGatedElement, route.id ?? undefined);

      if (route.index) {
        return (
          // Key aus der Map-Position (eindeutig), nicht route.id — siehe Path-Zweig:
          // mehrere Routes eines Plugins teilen sich dieselbe route.id für den Gate-Lookup.
          <Route
            key={`plugin-index-${index}`}
            index
            element={gatedElement}
          />
        );
      }

      if (!route.path) {
        return null;
      }

      return (
        // Key bewusst aus dem Pfad (eindeutig) statt route.id — Plugins mit mehreren
        // Routes teilen sich dieselbe route.id (= Plugin-Id) für den RoleGate-Lookup.
        <Route key={`plugin-path-${route.path}`} path={route.path} element={gatedElement}>
          {route.children ? renderPluginRoutes(route.children) : null}
        </Route>
      );
    })
    .filter(Boolean);
}

function App() {
  const pluginRoutes = usePluginRoutes();

  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToHash />
        <PluginBootstrapper plugins={builtinPlugins} hostVersion={APP_VERSION} />
        <Routes>
          {/* Public Routes - Landing Page & Marketing */}
          <Route
            path="/"
            element={
              <Suspense fallback={<LandingSkeleton />}>
                <LandingPage />
              </Suspense>
            }
          />
          <Route
            path="/pricing"
            element={
              <Suspense fallback={<LandingSkeleton />}>
                <Pricing />
              </Suspense>
            }
          />
          <Route
            path="/contact"
            element={
              <Suspense fallback={<LandingSkeleton />}>
                <Contact />
              </Suspense>
            }
          />
          <Route
            path="/impressum"
            element={
              <Suspense fallback={<LandingSkeleton />}>
                <Impressum />
              </Suspense>
            }
          />
          <Route
            path="/datenschutz"
            element={
              <Suspense fallback={<LandingSkeleton />}>
                <Datenschutz />
              </Suspense>
            }
          />

          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              <Suspense fallback={<AppSkeleton />}>
                <LoginPage />
              </Suspense>
            }
          />

          {/* Protected Application Routes */}
          <Route
            path="/app"
            element={
              <Suspense fallback={<AppSkeleton />}>
                <ProtectedRoute>
                  <WorkspaceGate>
                    <AppShell />
                  </WorkspaceGate>
                </ProtectedRoute>
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <Navigate to="onboarding" replace />
                </Suspense>
              }
            />
            <Route
              path="onboarding"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <WorkspaceOnboarding />
                </Suspense>
              }
            />
            <Route
              path="workspaces"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <WorkspaceManagerPage />
                </Suspense>
              }
            />
            <Route
              path="domains"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <RoleGate minOrgRole="Admin">
                    <DomainManagerPage />
                  </RoleGate>
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <SettingsPage />
                </Suspense>
              }
            >
              <Route
                index
                element={<Navigate to="account" replace />}
              />
              <Route
                path="account"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsAccountTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsWorkspaceTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace/mcp"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsWorkspaceMcpTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace/ki-overrides"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsWorkspaceKiOverridesTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace/retrieval-semantics"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsRetrievalSemanticsTab />
                  </Suspense>
                }
              />
              <Route
                path="workspace/members"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsWorkspaceMembersTab />
                  </Suspense>
                }
              />
              <Route
                path="plugins"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsPluginsTab />
                  </Suspense>
                }
              />
              <Route
                path="appearance"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <SettingsAppearanceTab />
                  </Suspense>
                }
              />
              <Route
                path="platform"
                element={<Navigate to="/app/settings/platform/ki-models" replace />}
              />
              {/* DevPlan 0120: Legacy-Pfade auf neue IA umlenken. */}
              <Route
                path="platform/models"
                element={<Navigate to="/app/settings/platform/ki-models" replace />}
              />
              <Route
                path="platform/system"
                element={<Navigate to="/app/settings/platform/version" replace />}
              />
              {/* Plattform · KI */}
              <Route
                path="platform/ki-models"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <KiModelsPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/ki-role-bindings"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <KiRoleBindingsPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/ki-voice"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <KiVoicePage />
                    </RoleGate>
                  </Suspense>
                }
              />
              {/* Plattform · Infrastruktur (unverändert) */}
              <Route
                path="platform/integrations"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <SettingsPlatformIntegrationsTab />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/databases"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <SettingsPlatformDatabasesTab />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/pipeline"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <SettingsPlatformPipelineTab />
                    </RoleGate>
                  </Suspense>
                }
              />
              {/* Plattform · System */}
              <Route
                path="platform/version"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <VersionBuildPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/audit"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <AuditLogPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              {/* Plattform · Operations */}
              <Route
                path="platform/provider-health"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <ProviderHealthPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/observability"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <SettingsPlatformObservabilityTab />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="platform/consistency"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <ConsistencyPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="members"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    {/* DevPlan 0139/T4 (F8): Org-Mitgliederverwaltung ist eine Admin-Seite
                        (Nav-Link ohnehin nur für Admins) — RoleGate ergänzt, damit sie nicht
                        per Direkt-URL erreichbar ist. Konsistent mit members/overview. */}
                    <RoleGate minOrgRole="Admin">
                      <OrgMembersPage />
                    </RoleGate>
                  </Suspense>
                }
              />
              <Route
                path="members/overview"
                element={
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <RoleGate minOrgRole="Admin">
                      <AdminMembersOverviewPage />
                    </RoleGate>
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="workspaces/:workspaceId/members"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <WorkspaceMembersPage />
                </Suspense>
              }
            />
            <Route
              path="plugins/marketplace"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <PluginMarketplace />
                </Suspense>
              }
            />
            <Route
              path="help"
              element={
                pluginRegistry.isPluginEnabled('help-assistant') ? (
                  <Suspense fallback={<div className="p-6">Laden...</div>}>
                    <ErrorBoundary featureName="Hilfe">
                      <HelpPage />
                    </ErrorBoundary>
                  </Suspense>
                ) : (
                  <Navigate to="/app/dashboard" replace />
                )
              }
            />
            <Route
              path="access-denied"
              element={
                <Suspense fallback={<div className="p-6">Laden...</div>}>
                  <AccessDeniedPage />
                </Suspense>
              }
            />
            {renderPluginRoutes(pluginRoutes)}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
            {/* Future routes will be added here:
            <Route path="graph" element={<GraphPage />} />
            <Route path="search" element={<SearchPage />} />
            */}
          </Route>
        </Routes>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
